import React, { useState, useRef, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import { useCreateAdminPurchase, useSupplyVendorsDropdown } from "../../hooks/useInventory";
import { useBranchProductUnits } from "../../hooks/useHeadOffice";

const emptyItem = { product_id: "", product_name: "", quantity: "", cost_price: "", notes: "" };

const AdminPurchaseCreate = () => {
  const navigate = useNavigate();
  const createMutation = useCreateAdminPurchase();
  const { data: vendorsData } = useSupplyVendorsDropdown();
  const { data: productsData } = useBranchProductUnits(1000);

  const vendors = vendorsData?.data || [];
  const products = productsData?.data?.data || [];

  const [form, setForm] = useState({
    supply_vendor_id: "",
    purchase_date: "",
    discount_amount: "",
    amount_paid: "",
    notes: "",
    reference_number: "",
  });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [errors, setErrors] = useState({});
  const [productSearch, setProductSearch] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    const handler = (e) => {
      if (openDropdown !== null && dropdownRefs.current[openDropdown] && !dropdownRefs.current[openDropdown].contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openDropdown]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleItemChange = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const addItem = () => setItems((prev) => [...prev, { ...emptyItem }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const selectProduct = (idx, product) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, product_id: product.id, product_name: product.name } : it));
    setOpenDropdown(null);
    setProductSearch((p) => ({ ...p, [idx]: "" }));
  };

  const filteredProducts = (idx) => {
    const s = (productSearch[idx] || "").toLowerCase();
    if (!s) return products;
    return products.filter((p) => p.name?.toLowerCase().includes(s) || p.product_categories_name?.toLowerCase().includes(s));
  };

  const totalCost = items.reduce((sum, it) => sum + (parseFloat(it.quantity || 0) * parseFloat(it.cost_price || 0)), 0);

  const validate = () => {
    const errs = {};
    if (!form.supply_vendor_id) errs.supply_vendor_id = "Vendor is required";
    if (!items.length) errs.items = "At least one item is required";
    items.forEach((it, i) => {
      if (!it.product_id) errs[`item_${i}_product`] = "Product required";
      if (!it.product_name) errs[`item_${i}_name`] = "Product name required";
      if (!it.quantity || parseInt(it.quantity) < 1) errs[`item_${i}_qty`] = "Quantity required";
      if (!it.cost_price || parseFloat(it.cost_price) <= 0) errs[`item_${i}_price`] = "Cost price required";
    });
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      supply_vendor_id: parseInt(form.supply_vendor_id),
      purchase_date: form.purchase_date || undefined,
      discount_amount: form.discount_amount ? parseFloat(form.discount_amount) : undefined,
      amount_paid: form.amount_paid ? parseFloat(form.amount_paid) : undefined,
      notes: form.notes || undefined,
      reference_number: form.reference_number || undefined,
      items: items.map((it) => ({
        product_id: parseInt(it.product_id),
        product_name: it.product_name,
        quantity: parseInt(it.quantity),
        cost_price: parseFloat(it.cost_price),
        notes: it.notes || undefined,
      })),
    };

    createMutation.mutate(payload, { onSuccess: () => navigate("/head-office/inventory/admin-purchases") });
  };

  const inputCls = (err) => `w-full border ${err ? "border-red-500" : "border-gray-300"} rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1`;

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/head-office/inventory/admin-purchases")} className="text-orange1 hover:text-orange-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold">New Purchase Order</h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Header info */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h2 className="font-semibold text-gray-700 mb-4">Order Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor <span className="text-red-500">*</span></label>
                <select name="supply_vendor_id" value={form.supply_vendor_id} onChange={handleFormChange} className={inputCls(errors.supply_vendor_id)}>
                  <option value="">Select vendor...</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                {errors.supply_vendor_id && <p className="text-red-500 text-xs mt-1">{errors.supply_vendor_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input type="date" name="purchase_date" value={form.purchase_date} onChange={handleFormChange} className={inputCls()} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                <input name="reference_number" value={form.reference_number} onChange={handleFormChange} className={inputCls()} placeholder="Optional" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Amount (₦)</label>
                <input type="number" name="discount_amount" value={form.discount_amount} onChange={handleFormChange} min="0" step="0.01" className={inputCls()} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid (₦)</label>
                <input type="number" name="amount_paid" value={form.amount_paid} onChange={handleFormChange} min="0" step="0.01" className={inputCls()} placeholder="0.00" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleFormChange} rows={2} className={inputCls()} placeholder="Optional notes..." />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-gray-700">Purchase Items</h2>
              <button type="button" onClick={addItem} className="text-orange1 hover:text-orange-600 text-sm font-medium">
                + Add Item
              </button>
            </div>
            <div className="space-y-4">
              {items.map((item, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {/* Product search dropdown */}
                    <div className="md:col-span-2 relative" ref={(el) => (dropdownRefs.current[idx] = el)}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Product <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={openDropdown === idx ? (productSearch[idx] || "") : (item.product_name || "")}
                        onFocus={() => setOpenDropdown(idx)}
                        onChange={(e) => { setProductSearch((p) => ({ ...p, [idx]: e.target.value })); setOpenDropdown(idx); }}
                        className={inputCls(errors[`item_${idx}_product`])}
                        placeholder="Search product..."
                      />
                      {openDropdown === idx && (
                        <div className="absolute left-0 right-0 mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-48 overflow-y-auto z-[100]" style={{ backgroundColor: '#ffffff', opacity: 1 }}>
                          {filteredProducts(idx).slice(0, 30).map((p) => (
                            <div
                              key={p.id}
                              onClick={() => selectProduct(idx, p)}
                              className="px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm border-b border-gray-100 last:border-0"
                            >
                              <span className="font-medium">{p.name}</span>
                              {p.product_categories_name && <span className="text-gray-400 text-xs ml-2">— {p.product_categories_name}</span>}
                            </div>
                          ))}
                          {filteredProducts(idx).length === 0 && (
                            <div className="px-3 py-3 text-gray-400 text-sm text-center">No products found</div>
                          )}
                        </div>
                      )}
                      {errors[`item_${idx}_product`] && <p className="text-red-500 text-xs mt-1">{errors[`item_${idx}_product`]}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Quantity <span className="text-red-500">*</span></label>
                      <input type="number" value={item.quantity} onChange={(e) => handleItemChange(idx, "quantity", e.target.value)} min="1" className={inputCls(errors[`item_${idx}_qty`])} placeholder="0" />
                      {errors[`item_${idx}_qty`] && <p className="text-red-500 text-xs mt-1">{errors[`item_${idx}_qty`]}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cost Price (₦) <span className="text-red-500">*</span></label>
                      <input type="number" value={item.cost_price} onChange={(e) => handleItemChange(idx, "cost_price", e.target.value)} min="0.01" step="0.01" className={inputCls(errors[`item_${idx}_price`])} placeholder="0.00" />
                      {errors[`item_${idx}_price`] && <p className="text-red-500 text-xs mt-1">{errors[`item_${idx}_price`]}</p>}
                    </div>

                    <div className="flex flex-col">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Subtotal</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-semibold text-gray-800 flex-1 flex items-center">
                        ₦{((parseFloat(item.quantity || 0) * parseFloat(item.cost_price || 0))).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-3 gap-3">
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => handleItemChange(idx, "notes", e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange1"
                      placeholder="Item notes (optional)"
                    />
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 text-sm">
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-3">
                <span className="text-gray-500 text-sm mr-4">Total Cost:</span>
                <span className="text-xl font-bold text-gray-900">₦{totalCost.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate("/head-office/inventory/admin-purchases")} className="px-5 py-2 border border-gray-300 rounded hover:bg-gray-50 transition text-sm">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isLoading} className="px-5 py-2 bg-orange1 text-white rounded hover:bg-orange-600 transition text-sm disabled:opacity-50">
              {createMutation.isLoading ? "Creating..." : "Create Purchase Order"}
            </button>
          </div>
        </form>
      </div>
    </MainLayouts>
  );
};

export default AdminPurchaseCreate;
